import type {PanelSnapshotDto} from '@devmil/contracts';
import {
  ChartNoAxesCombined,
  LayoutDashboard,
  MessagesSquare,
  Radar,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import type {ComponentType} from 'react';

import {MapaPage} from '../pages/MapaPage.js';
import {MetricsPage} from '../pages/MetricsPage.js';
import {OperationPage} from '../pages/OperationPage.js';
import {TodayPage} from '../pages/TodayPage.js';
import {ViralRadarPage} from '../pages/ViralRadarPage.js';

/**
 * As páginas com entrada de menu, nesta ordem e com estes nomes.
 *
 * As quatro primeiras são as da prévia aprovada. A quinta, Virais, chegou
 * depois e paga o preço declarado: item novo aqui muda a faixa `.dm-nav`, que
 * aparece nas quatro telas travadas por comparação de pixel, então ele exige o
 * recongelamento do baseline pelo rito de `docs/runbooks/
 * panel-visual-review.md`. Quem entrar nesta tabela paga o mesmo preço.
 *
 * A tabela mora fora de `router.tsx` porque a navegação também a lê, e um
 * import circular entre rota e shell é o tipo de coisa que quebra em produção
 * e passa em teste.
 */
export interface PanelRoute {
  id: 'today' | 'mapa' | 'metrics' | 'operation' | 'radar';
  path: string;
  navLabel: string;
  Icon: LucideIcon;
  title: string;
  /**
   * O selo do canto direito do título, decidido pelo SNAPSHOT.
   *
   * Era um texto cravado por rota, e por isso a página Hoje carimbava "DADOS
   * SIMULADOS" em cima da fila real de aprovação: o dono lia "simulado" e
   * aprovava peça de verdade. A flag `illustrative` já existia em cada bloco do
   * contrato e nenhum componente a lia, e rótulo que ninguém desenha não é
   * cerca.
   *
   * A regra vale nas duas direções: conteúdo ilustrativo continua identificado
   * como ilustrativo, e dado medido não pode sair carimbado de simulado.
   */
  badge: (snapshot: PanelSnapshotDto) => string;
  subtitle: (snapshot: PanelSnapshotDto) => string;
  /** Corpo da página. Preenchido pelos tasks 4, 5 e 6. */
  Body?: ComponentType;
}

const MONTHS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const;

const WEEKDAYS = [
  'Domingo',
  'Segunda',
  'Terça',
  'Quarta',
  'Quinta',
  'Sexta',
  'Sábado',
] as const;

/** `2026-08-27` vira `Quinta, 27 de agosto`, sem depender do fuso da máquina. */
export const formatQueueDate = (isoDate: string): string => {
  const [year, month, day] = isoDate.split('-').map(Number) as [
    number,
    number,
    number,
  ];
  const weekday =
    WEEKDAYS[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];
  return `${weekday}, ${day} de ${MONTHS[month - 1]}`;
};

/**
 * "1 Reel", "3 Reels", "1 carrossel", "2 carrosséis".
 *
 * O plural é `+s` por padrão porque é o que serve a Reel, Storie e foto. O
 * terceiro parâmetro existe para as palavras em que a regra não vale, e
 * "carrosséis" é a primeira delas: "carrossels" não é português.
 */
const plural = (count: number, singular: string, plural?: string) =>
  `${count} ${count === 1 ? singular : (plural ?? `${singular}s`)}`;

/** O selo do desenho aprovado, para bloco que veio do snapshot ilustrativo. */
const SELO_SIMULADO = 'DADOS SIMULADOS';

/**
 * O selo do bloco medido.
 *
 * "MEDIDOS", e não "REAIS", porque é isso que a flag afirma e é o vocabulário
 * que o resto do sistema já usa: o bloco virou quando existiu leitura do banco.
 * Dentro dele, campo que a coleta não trouxe continua mostrando "Indisp.", e um
 * selo dizendo "DADOS REAIS" em cima de uma tela com três "Indisp." prometeria
 * mais do que a medição entregou.
 *
 * O selo NÃO some quando o dado é medido: o desenho aprovado reserva o espaço,
 * e uma tela que só rotula o simulado ensina o leitor a não procurar rótulo
 * nenhum. Quem lê precisa saber de onde veio o número nos dois casos.
 */
const SELO_MEDIDO = 'DADOS MEDIDOS';

const seloDoBloco = (illustrative: boolean) =>
  illustrative ? SELO_SIMULADO : SELO_MEDIDO;

export const PANEL_ROUTES: readonly PanelRoute[] = [
  {
    id: 'today',
    path: '/hoje',
    navLabel: 'Hoje',
    Icon: LayoutDashboard,
    title: 'Fila de hoje',
    badge: ({today}) => seloDoBloco(today.illustrative),
    /*
      A COMPOSIÇÃO do dia, com a foto e o carrossel dentro.

      Ela anunciava "3 Reels e 3 Stories" desde antes de a foto existir, e
      continuou anunciando isso depois que a grade passou a ter sete peças em
      2026-09-05: o subtítulo dizia seis e a fila mostrava sete cartões (achado
      F12, passo 7). O carrossel entrou em 2026-09-06 e traz o mesmo risco: oito
      cartões anunciados como sete.

      O carrossel só aparece na frase quando o dia TEM um. Ele nasce desligado
      (`DEVMIL_CAROUSEL_ENABLED`), e anunciar "0 carrosséis" num dia que não tem
      nenhum encheria o subtítulo de uma informação que não é sobre o dia.
    */
    subtitle: ({today}) =>
      [
        `${formatQueueDate(today.date)} · ${plural(today.mix.reels, 'Reel')}`,
        plural(today.mix.stories, 'Storie'),
        plural(today.mix.photos, 'foto'),
        ...(today.mix.carousels > 0
          ? [plural(today.mix.carousels, 'carrossel', 'carrosséis')]
          : []),
      ]
        .join(', ')
        // A última vírgula vira "e", como em português se escreve lista.
        .replace(/, ([^,]*)$/u, ' e $1'),
    Body: TodayPage,
  },
  {
    id: 'mapa',
    path: '/mapa',
    navLabel: 'MAPA',
    Icon: MessagesSquare,
    title: 'Funil MAPA',
    badge: ({mapa}) => seloDoBloco(mapa.illustrative),
    subtitle: () =>
      'Conversas iniciadas pelo usuário, sem abordagem automática',
    Body: MapaPage,
  },
  {
    id: 'metrics',
    path: '/metricas',
    navLabel: 'Métricas',
    Icon: ChartNoAxesCombined,
    title: 'Métricas e aprendizado',
    badge: ({metrics}) => seloDoBloco(metrics.illustrative),
    subtitle: ({metrics}) =>
      `Primeiros ${metrics.windowDays} dias formam a linha de base`,
    Body: MetricsPage,
  },
  {
    id: 'operation',
    path: '/operacao',
    navLabel: 'Operação',
    Icon: ShieldCheck,
    title: 'Operação e autonomia',
    // A única que NÃO muda com o snapshot: este selo não fala de origem de
    // dado, fala de quem decide. `operationDashboard.illustrative` é sempre
    // falso (o bloco é sempre medido), e trocar o texto por "DADOS MEDIDOS"
    // apagaria da tela o aviso de que elevar nível é decisão do Thiago.
    badge: () => 'CONTROLE HUMANO',
    subtitle: () => 'Elevação de nível depende de decisão manual do Thiago',
    Body: OperationPage,
  },
  {
    id: 'radar',
    path: '/virais',
    navLabel: 'Virais',
    Icon: Radar,
    title: 'Radar de virais',
    // Constante porque o radar NÃO está no snapshot: ele tem endpoint próprio
    // (`GET /v1/panel/radar`) e credencial própria, e pode estar indisponível
    // com o resto do painel inteiro medido. `badge` e `subtitle` só recebem o
    // snapshot, então uma função aqui não teria como saber se a leitura do dia
    // foi medida ou ilustrativa: ler a `illustrative` do bloco errado é o
    // defeito que carimbou "DADOS SIMULADOS" na fila real de aprovação.
    //
    // Por isso este selo não fala de origem de dado, e sim de origem de
    // CONTEÚDO: o que esta página mostra foi lido da plataforma, e não medido
    // por nós nem produzido por nós. O selo de simulado ou medido é desenhado
    // dentro da página, onde a resposta do radar chega.
    badge: () => 'LEITURA DA PLATAFORMA',
    subtitle: () =>
      'O radar lê e ranqueia. Publicar continua sendo gesto seu, no aplicativo',
    Body: ViralRadarPage,
  },
];
